import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { shallowEqual, useSelector } from 'react-redux';
import { checkEnvPurpleZone, getCookie, isDefined, mergeDeep, setCookie } from '../common/Utils';
import { PURPLEZONE_CHARACTER_ID_KEY, PURPLEZONE_SERVER_ID_KEY } from '../common/constants';
import { Pagination } from '../components/Pagination';
import { IServerDDProp, ServerDropdown } from '../components/ServerDropdown';
import useAxios from '../hook/useAxios';
import useRouter from '../hook/useRouter';
import { ICollectionItemList } from '../interface/ICollection';
import { ISearchParam } from '../interface/ICommon';
import { RootState, store, useAppDispatch } from '../store';
import { setCollectionParams } from '../store/collectionSlice';
import { CollectionList } from './CollectionList';
import { ResultWrapFavorite } from './ResultWrapFavorite';

const makeParams = (_obj: ISearchParam, _key: string, _val: string | number) => {
    if (_val) {
        _obj[_key] = _val;
    }
};

const getFirstSvrId = (_service: string) => {
    if (checkEnvPurpleZone()) {
        // case: 퍼플존 (페이지 진입시, sessionStorage에 저장된 pzsid 값 사용)
        const pzsid = sessionStorage.getItem(PURPLEZONE_SERVER_ID_KEY);
        if (pzsid) return pzsid;
    }

    if (window.userData?.gameAccount?.serverId) {
        return window.userData.gameAccount.serverId;
    }

    const recentSvrId: string | boolean = getCookie('gameinforecentsvrid' + _service);
    if (recentSvrId !== false) {
        return recentSvrId as string;
    }

    return '';
};

export const ResultWrap = () => {
    const l10n = useSelector((state: RootState) => state.main.l10n);
    const config = store.getState().main.config;
    const dispatch = useAppDispatch();
    const { res, loading, error, fetchData } = useAxios<ICollectionItemList>();
    const collectionParams = useSelector((state: RootState) => state.collection.collectionParams, shallowEqual);
    const service = useSelector((state: RootState) => state.main.config.service);
    const firSvrId = useMemo(() => getFirstSvrId(service), []);

    const [svrid, svridSet] = useState('');
    const { params, getValue, changeParam } = useRouter([
        'grades',
        'incomplete',
        'options',
        'query',
        'page',
        'exactsearch',
        'favorite',
        'fromitemid',
    ]);

    useEffect(() => {
        if (!params) return;

        let para = { size: 30 };
        if (getValue('fromitemid')) {
            makeParams(para, 'itemId', getValue('fromitemid'));
        } else {
            makeParams(para, 'grades', getValue('grades'));
            makeParams(para, 'options', getValue('options'));
            makeParams(para, 'searchKeyword', getValue('query'));
            makeParams(para, 'page', getValue('page'));
            makeParams(para, 'favorite', getValue('favorite'));
            makeParams(para, 'exact', getValue('exactsearch'));

            let isGameAccount = false;
            if (checkEnvPurpleZone()) {
                // case : 퍼플존 (플레이중 캐릭터의 server id 적용)
                const purpleZoneServerId = sessionStorage.getItem(PURPLEZONE_SERVER_ID_KEY) || '';
                isGameAccount = purpleZoneServerId !== '';
            } else {
                isGameAccount = window.userData.gameAccount.serverId !== '';
            }

            makeParams(para, 'incomplete', getValue('incomplete') || (isGameAccount ? 'true' : 'false'));
        }

        dispatch(setCollectionParams(para));
    }, [params]);

    useEffect(() => {
        if (!collectionParams.size) return;

        if (collectionParams.incomplete === 'true') {
            if (window.isLoginFlag === 'false') {
                window.GNBLogin();
                return;
            } else {
                if (!window.userData.gameAccount.serverId) {
                    changeParam({ incomplete: 'false' });
                    toast.dismiss();
                    toast(
                        (t) => (
                            <div className="toast-wrapper">
                                <span className="toast-title">{l10n['noti.choosecharacters']}</span>
                            </div>
                        ),
                        {
                            duration: 3000,
                            className: 'gameinfo-toast',
                            position: 'bottom-center',
                        }
                    );
                    return;
                }
            }
        }

        let collectionApiParams = mergeDeep({}, collectionParams) as typeof collectionParams;
        if (checkEnvPurpleZone()) {
            // case : 퍼플존 (/v2.0/dict/search/collection API parameter - characterid, serverid 추가)
            // @see https://sb-api-goats.plaync.com/search/docs/integrated.html#_request_parameters_14
            const characterId = sessionStorage.getItem(PURPLEZONE_CHARACTER_ID_KEY);
            const serverId = sessionStorage.getItem(PURPLEZONE_SERVER_ID_KEY);

            collectionApiParams = {
                ...collectionApiParams,
                ...(isDefined(characterId) && { characterId }),
                ...(isDefined(serverId) && { serverId }),
            };
        }

        fetchData({
            method: 'get',
            config: store.getState().main.config,
            l10n: l10n,
            path: `/${config.service}/v2.0/dict/search/collection`,
            param: collectionApiParams,
        });
    }, [collectionParams]);

    useEffect(() => {
        if (error) {
            toast.dismiss();
            toast(
                (t) => (
                    <div className="toast-wrapper">
                        <span className="toast-title" dangerouslySetInnerHTML={{ __html: error.msg }}></span>
                    </div>
                ),
                {
                    duration: 3000,
                    className: 'gameinfo-toast',
                    position: 'bottom-center',
                }
            );
        }
    }, [error]);

    const svrDropdownProp: IServerDDProp = {
        defaultSvrId: firSvrId,
        changeSvrId: svrid,
        tradableWorld: false,
        useAllSvrBtn: false,
        useWorldSvr: false,
        submitCallback: (_svr: string) => {
            changeParam({ serverid: _svr, page: '' });
            setCookie('gameinforecentsvrid' + service, _svr, 7, '.plaync.com');
        },
    };

    if (!res) return null;

    return (
        <div className="gameinfo-result-wrapper">
            <div className="gameinfo-result">
                <section className="gameinfo-result__util">
                    <ServerDropdown props={svrDropdownProp} />
                    <div className="title">
                        {l10n['noti.searchresults']}&nbsp;
                        <em
                            dangerouslySetInnerHTML={{
                                __html: l10n['noti.per'].replace(
                                    '{N}',
                                    `${res.pagination.total > res.pagination.limit ? res.pagination.limit + '+' : res.pagination.total}`
                                ),
                            }}
                        ></em>
                    </div>
                    <ResultWrapFavorite />
                </section>

                <CollectionList listData={res.contents} collectionListParam={collectionParams} loading={loading} />

                {res.contents && res.contents.length > 0 && <Pagination dat={res.pagination} />}
            </div>
        </div>
    );
};
